/**
 * THE DESK V3 — full-screen free-form tile sandbox. MJ arranges smart tiles
 * anywhere by drag; the system informs, HE decides placement. Internal tool,
 * maximal visual energy behind the Motion/Still toggle. PREVIEW ONLY.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGrid = WidthProvider(Responsive);
import { DUR, EASE, motionEnabled, setMotionEnabled, reduced, ensureMotionStyles } from './studioMotion';
import { Chrome } from './V2AdminOSPage';
import { ListTile, DemoBadge } from './V2DeskLists';
import {
  BG, BORDER, TXT, SUB, TEAL, AMBER, MAGENTA, VIOLET, SPEC, HEAD, TYPE_META, mini,
  NowTile, PulseTile, ClientsTile, SingleClientTile, MoneyTile, ParkingTile,
  WinsTile, NotesTile, CapturePadTile, DeskAITile, makeScrub,
} from './V2DeskTiles';
import { ClockTile, WinsWeekTile, SendsTodayTile, UnpaidInvoicesTile, EnquiriesTile, AwaitingReplyTile, PromisesTile, CalendarTile, GhlEnquiriesTile, GhlCampaignsTile, GhlPipelineTile, WfmBayTile, MoneyPipelinesTile } from './V2DeskWidgets';
import { MoneyXeroTile, MoneyXeroDetailTile } from './V2DeskXeroTiles';
import { SourceMark, sourceFor } from './V2DeskSourceMark';
import { useActiveProfile, ProfileSwitcher } from './V2DeskProfiles';
import { PaymentsTile, InboxTile, PipelineTile, QuotePipelineTile, FittingBayTile, GillilandWorldTile, QuotesAgingTile, FocusTimerTile, QuickLinksTile, WeatherTile, WonChartTile, RepliesTrendTile, JobsTile, ProjectsTile, LLMBudgetTile, SweepAuditTile } from './V2DeskWidgets2';
import { DebriefTile, DebriefView, TodayActionsTile } from './V2DeskDebrief';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('kindra-admin-token') || ''}` });
const req = async (method, path, body, _retried) => {
  const r = await fetch(`${API}${path}`, { method, headers: auth(), ...(body ? { body: JSON.stringify(body) } : {}) });
  const d = await r.json().catch(() => ({}));
  if (r.status === 401) {
    if (!_retried) {
      const { trySilentRefresh } = await import('./authRefresh');
      if (await trySilentRefresh()) return req(method, path, body, true);
    }
    localStorage.removeItem('kindra-admin-token');
    window.location.assign('/admin/login?returnTo=%2Fadmin%2Fdesk');
    throw new Error('auth');
  }
  if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
  return d;
};

const STATIC_TILES = {
  now: { label: 'Now — what needs you', accent: MAGENTA, w: 8, h: 4 },
  pulse: { label: "Today's pulse", accent: MAGENTA, w: 4, h: 5 },
  clients: { label: 'Clients', accent: AMBER, w: 8, h: 4 },
  money: { label: 'Money ticker', accent: TEAL, w: 4, h: 2 },
  parking: { label: '🅿 Parking lot', accent: MAGENTA, w: 8, h: 2 },
  wins: { label: '🏆 Win wall', accent: AMBER, w: 8, h: 3 },
  ai: { label: 'Desk AI — concierge', accent: VIOLET, w: 4, h: 9 },
  capture: { label: 'Quick capture', accent: TEAL, w: 4, h: 3 },
  notes: { label: 'Notes scratchpad', accent: SUB, w: 4, h: 4 },
  clock: { label: '🕐 Clock', accent: TEAL, w: 2, h: 2 },
  winsweek: { label: '🏆 Wins this week', accent: AMBER, w: 2, h: 2 },
  sendstoday: { label: '📤 Sends today', accent: TEAL, w: 4, h: 2 },
  invoices: { label: '💸 Unpaid invoices', accent: AMBER, w: 4, h: 4 },
  enquiries: { label: '📥 New enquiries', accent: TEAL, w: 4, h: 5 },
  awaiting: { label: '⏳ Awaiting reply', accent: MAGENTA, w: 4, h: 4 },
  promises: { label: '🤝 Promises & deadlines', accent: AMBER, w: 4, h: 4 },
  calendar: { label: "📆 Today's calendar", accent: VIOLET, w: 4, h: 6 },
  payments: { label: '💵 Payments feed', accent: TEAL, w: 4, h: 4 },
  inbox: { label: '📬 Inbox pulse', accent: MAGENTA, w: 4, h: 3 },
  pipeline: { label: '🛠 Pipeline', accent: VIOLET, w: 4, h: 4 },
  quotepipeline: { label: '◧ Quote Pipeline', accent: MAGENTA, w: 12, h: 6 },
  fittingbay: { label: '🔧 Fitting-bay board', accent: AMBER, w: 12, h: 6 },
  gilliland: { label: '🧭 Gilliland World', accent: TEAL, w: 4, h: 6 },
  quotesaging: { label: '⏱ Quotes out — aging', accent: AMBER, w: 4, h: 4 },
  jobs: { label: '🛠 Jobs — stage board', accent: TEAL, w: 4, h: 4 },
  focustimer: { label: '🍅 Focus timer', accent: TEAL, w: 2, h: 4 },
  quicklinks: { label: '🔗 Quick links', accent: VIOLET, w: 4, h: 3 },
  weather: { label: '🌤 Brisbane weather', accent: TEAL, w: 3, h: 2 },
  debrief: { label: '🎧 The Debrief', accent: VIOLET, w: 4, h: 3 },
  actions: { label: '✅ Today\'s actions', accent: AMBER, w: 4, h: 5 },
  projects: { label: '◈ Projects', accent: TEAL, w: 4, h: 4 },
  llmbudget: { label: '⧉ AI budget', accent: AMBER, w: 3, h: 2 },
  sweepaudit: { label: '🧹 Sweep audit', accent: SUB, w: 4, h: 3 },
  wonchart: { label: '📊 Won by month', accent: AMBER, w: 4, h: 3 },
  repliestrend: { label: '📈 Replies trend', accent: TEAL, w: 4, h: 3 },
  moneyxero: { label: 'Money · Xero', accent: '#7BB0EC', w: 4, h: 3 },
  moneyxerodetail: { label: 'Money · Xero — Detail', accent: '#7BB0EC', w: 4, h: 5 },
  ghlenquiries: { label: '📥 GHL enquiries', accent: TEAL, w: 4, h: 5 },
  ghlcampaigns: { label: '📣 GHL campaigns', accent: VIOLET, w: 4, h: 4 },
  ghlpipeline: { label: '🛠 GHL pipeline', accent: VIOLET, w: 4, h: 4 },
  wfmbay: { label: '🏗 Fitting bay (WFM)', accent: TEAL, w: 4, h: 5 },
  moneypipelines: { label: '💰 Money · pipelines', accent: AMBER, w: 4, h: 3 },
};
const WIDGET_META = {
  now: ['work', 'The single thing that needs you'], pulse: ['work', "Today's ranked attention feed"],
  parking: ['work', 'Ideas resting, not lost'], wins: ['work', 'Completed → celebrated'],
  capture: ['work', 'Type/speak → AI files it'], notes: ['work', 'Free scratchpad, autosaves'],
  clients: ['clients', 'Heat-ranked client cards'],
  money: ['money', 'Quotes out · won · unpaid'], invoices: ['money', 'Xero unpaid + aging (read-only)'],
  sendstoday: ['money', 'Sends vs warm-up cap'],
  enquiries: ['signals', 'Site intake/discover/contact feed'], awaiting: ['signals', 'Sent, no reply — chase from here'],
  promises: ['signals', 'Dated tasks counting down'], calendar: ['signals', 'MS 365 day view, native-styled'],
  clock: ['signals', 'Time & date, big'], winsweek: ['signals', 'Wins counter for the week'],
  payments: ['money', 'Stripe money landing — read-only'], inbox: ['signals', 'Unread + newest 3 (MS 365)'],
  pipeline: ['clients', 'Leads by stage — funnel bars'], quotesaging: ['money', 'Open quotes with heat by age'],
  quotepipeline: ['work', 'Build quotes by stage — drag to move'],
  fittingbay: ['work', 'Fitting-bay board — which rig, which stage'],
  gilliland: ['work', "Col's world — trips, plans, renos (private)"],
  jobs: ['work', 'Accepted quotes → delivery stages'],
  focustimer: ['work', 'Pomodoro ring — ADHD body-double'], quicklinks: ['work', 'Your pinned go-to links'],
  weather: ['signals', 'Brisbane, native-styled'], wonchart: ['money', 'Won by month bar chart'],
  repliestrend: ['signals', 'Reply trend, 8 weeks'],
  moneyxero: ['money', 'Bank · owed · overdue — live from Xero, tap → chase list'],
  moneyxerodetail: ['money', 'Full Xero cash picture — bank, owed, payables, net, month, oldest overdue'],
  debrief: ['signals', 'Fieldy day digest — lands 5pm'],
  actions: ['work', "Actions from today's Fieldy pass — one-tap to Desk"],
  projects: ['work', 'Every build Col has going — by stage, needs-attention nudge'],
  llmbudget: ['signals', 'TravelTrucks OS AI budget · account-level, honest'],
  sweepaudit: ['signals', 'What the background sweeps just did (or wisely skipped)'],
  ghlenquiries: ['signals', 'New GoHighLevel contacts — copy phone/email in one tap'],
  ghlcampaigns: ['signals', 'GoHighLevel campaign performance — sent · opened · replied'],
  ghlpipeline: ['clients', 'Opportunity pipeline — real GHL stages, count + $ per stage'],
  wfmbay: ['work', 'WorkflowMax fitting bay — jobs by state, hours this week'],
  moneypipelines: ['money', 'Sales pipeline signals — GHL + WFM, kept separate from Xero'],
  ai: ['ai', 'Concierge — files, nags, undoes'],
};
const LIB_CATS = [['work', '⚡', 'Work'], ['clients', '👤', 'Clients'], ['money', '💰', 'Money'], ['signals', '📡', 'Signals'], ['ai', '🤖', 'AI']];
const COMING_SOON = ['📞 Reception.ai missed calls', '📈 Reply-rate rankings', '🏦 Cash position'];
const DEFAULT_TILES = ['now', 'ai', 'pulse', 'money', 'clients', 'wins', 'parking'];
const DEFAULT_LG = [
  { i: 'now', x: 0, y: 0, w: 8, h: 4 }, { i: 'ai', x: 8, y: 0, w: 4, h: 9 },
  { i: 'pulse', x: 0, y: 4, w: 4, h: 5 }, { i: 'money', x: 4, y: 4, w: 4, h: 2 },
  { i: 'clients', x: 4, y: 6, w: 4, h: 3 }, { i: 'wins', x: 0, y: 9, w: 8, h: 3 },
  { i: 'parking', x: 0, y: 12, w: 8, h: 2 },
];
const BPS = ['xl', 'lg', 'md', 'sm'];

/** Focus ring — auto-starts 25m with the overlay; skip for quick tasks; breather at the end. */
const FocusRing = ({ showToast }) => {
  const [left, setLeft] = React.useState(25 * 60);
  const [phase, setPhase] = React.useState('work');
  const [skipped, setSkipped] = React.useState(false);
  React.useEffect(() => {
    if (skipped) return;
    const t = setInterval(() => setLeft((s) => {
      if (s > 1) return s - 1;
      if (phase === 'work') { setPhase('breather'); showToast('☕ 25 done — 5-min breather suggested.'); return 5 * 60; }
      setSkipped(true); showToast('🔨 Breather over — back in when ready.'); return 0;
    }), 1000);
    return () => clearInterval(t);
  }, [skipped, phase, showToast]);
  if (skipped) return null;
  const total = (phase === 'work' ? 25 : 5) * 60;
  const pct = 1 - left / total;
  const mm = String(Math.floor(left / 60)).padStart(2, '0'), ss = String(left % 60).padStart(2, '0');
  return (
    <div data-testid="focus-ring" style={{ margin: '0 auto 16px', width: 92 }}>
      <div style={{ width: 92, height: 92, borderRadius: '50%', display: 'grid', placeItems: 'center', background: `conic-gradient(${phase === 'work' ? TEAL : AMBER} ${pct * 360}deg, rgba(255,255,255,0.08) 0deg)` }}>
        <div style={{ width: 76, height: 76, borderRadius: '50%', background: BG, display: 'grid', placeItems: 'center' }}>
          <span style={{ fontFamily: HEAD, fontSize: 19, fontWeight: 800, color: TXT, fontVariantNumeric: 'tabular-nums' }}>{mm}:{ss}</span>
        </div>
      </div>
      <button data-testid="focus-skip-timer" onClick={() => setSkipped(true)}
        style={{ marginTop: 6, background: 'none', border: 'none', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, color: SUB, cursor: 'pointer', textDecoration: 'underline' }}>
        skip timer
      </button>
    </div>
  );
};
const DEFAULT_MD = DEFAULT_LG.map((it) => ({ ...it, w: Math.min(it.w, 8) }));
const DEFAULT_SM = DEFAULT_LG.map((it, idx) => ({ i: it.i, x: 0, y: idx * 6, w: 2, h: Math.max(it.h, 3) }));
const DEFAULT_LAYOUTS = { lg: DEFAULT_LG, md: DEFAULT_MD, sm: DEFAULT_SM };

const tileDef = (id, lists, clients) => {
  if (STATIC_TILES[id]) return STATIC_TILES[id];
  if (id.startsWith('list:')) {
    const l = lists.find((x) => x.list_id === id.slice(5));
    const proj = l?.kind === 'project';
    return { label: `${proj ? '▦' : '☑'} ${l?.name || (proj ? 'Project' : 'List')}`, accent: proj ? VIOLET : TEAL, w: proj ? 6 : 4, h: proj ? 6 : 5 };
  }
  if (id.startsWith('client:')) {
    const c = clients.find((x) => x.item_id === id.slice(7));
    return { label: `👤 ${c?.title || 'Client'}`, accent: AMBER, w: 3, h: 4 };
  }
  return { label: id, accent: SUB, w: 4, h: 4 };
};

const CrashProbe = ({ id }) => {
  if (localStorage.getItem('desk-crash-tile') === id) throw new Error('crash test');
  return null;
};

class TileBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) return (
      <div data-testid={`tile-crashed-${this.props.id}`} style={{ display: 'grid', placeItems: 'center', height: '100%', textAlign: 'center' }}>
        <div>
          <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: SUB, margin: '0 0 8px' }}>This tile hit a snag — the rest of the desk is fine.</p>
          <button data-testid={`tile-reload-${this.props.id}`} className="no-drag" onClick={() => this.setState({ error: null })} style={{ ...mini, color: TEAL, borderColor: 'rgba(84,209,151,0.4)' }}>↻ Reload tile</button>
        </div>
      </div>
    );
    return this.props.children;
  }
}

export default function V2DeskPage() {
  return (
    <Chrome activeTab="desk" fullBleed>
      <DeskCanvas />
    </Chrome>
  );
}

function DeskCanvas() {
  const profile = useActiveProfile(req);
  const activeProfileId = profile.activeId;
  const [board, setBoard] = useState(null);
  const [lists, setLists] = useState([]);
  const [listsLoaded, setListsLoaded] = useState(false);
  const [tiles, setTiles] = useState(DEFAULT_TILES);
  const [layouts, setLayouts] = useState(DEFAULT_LAYOUTS);
  const [removed, setRemoved] = useState([]);
  const [notes, setNotes] = useState('');
  const [ready, setReady] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  const [tick, setTick] = useState(0);
  const [arrange, setArrange] = useState(false);
  const [library, setLibrary] = useState(false);
  const [capOpen, setCapOpen] = useState(false);
  const [capVal, setCapVal] = useState('');
  const [capBusy, setCapBusy] = useState(false);
  const [confirmChip, setConfirmChip] = useState(null);
  const [listening, setListening] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [toast, setToast] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [delConfirm, setDelConfirm] = useState(null);
  const [delChain, setDelChain] = useState(null);
  const [focus, setFocus] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [motion, setMotion] = useState(motionEnabled());
  const [demo, setDemo] = useState(() => localStorage.getItem('desk-demo') === '1');
  const [debriefOpen, setDebriefOpen] = useState(false);
  const [installEvt, setInstallEvt] = useState(null);
  const [standalone] = useState(() => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true);
  const [orb, setOrb] = useState(null);
  const arrangeRef = useRef(false);
  const removedRef = useRef([]);
  const toastTimer = useRef(null);
  const saveTimer = useRef(null);
  const capRef = useRef(null);
  const anim = motion && !reduced();

  const showToast = useCallback((msg, undo) => {
    clearTimeout(toastTimer.current);
    setToast({ msg, undo });
    toastTimer.current = setTimeout(() => setToast(null), 6000);
  }, []);

  const load = useCallback(async () => {
    try {
      const [b, l] = await Promise.all([req('GET', '/api/admin/desk/board'), req('GET', '/api/admin/desk/lists')]);
      setBoard(b); setLists(l.lists || []); setListsLoaded(true); setLoadErr(null); setTick((t) => t + 1);
    } catch (e) { if (e.message !== 'auth') setLoadErr(e.message || 'load failed'); }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('kindra-admin-token')) {
      window.location.assign('/admin/login?returnTo=%2Fadmin%2Fdesk');
      return;
    }
    ensureMotionStyles();
    load();
  }, [load]);

  // Post-Xero-reconnect landing: if the URL carries `?xero=connected`
  // (backend adds it after saving the token), announce success and strip
  // the query param so a refresh doesn't re-toast. The Xero tiles read
  // this param themselves too and force-refresh their data (see
  // useMoneySummary in V2DeskXeroTiles.jsx).
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('xero') === 'connected') {
      showToast('✓ Xero connected — Money tile refreshing…');
      url.searchParams.delete('xero');
      window.history.replaceState({}, '', url.pathname + (url.search ? url.search : '') + url.hash);
    }
  }, [showToast]);

  // Load the layout for the ACTIVE profile. Refires whenever MJ flips the
  // switcher (or the auto-picker settles on a profile), which lets the
  // Desk swap layouts instantly without a hard reload.
  useEffect(() => {
    if (!activeProfileId) return;
    setReady(false);
    req('GET', `/api/admin/desk/layout?profile=${encodeURIComponent(activeProfileId)}`).then((d) => {
      // Persistence discipline: saved state ALWAYS wins. `d.tiles` may be
      // an empty array (MJ removed everything) — that must be respected,
      // not silently replaced with DEFAULT_TILES. Also dedupe defensively
      // in case a previous bug persisted duplicates (ghost tiles).
      if (Array.isArray(d.tiles)) {
        const seen = new Set();
        const clean = d.tiles.filter((id) => {
          if (typeof id !== 'string' || !id) return false;
          if (seen.has(id)) return false;
          seen.add(id); return true;
        });
        setTiles(clean);
      } else {
        setTiles(DEFAULT_TILES);
      }
      if (d.layouts && Object.keys(d.layouts).length) {
        // Same discipline for layouts — dedupe by `i` per breakpoint
        const cleanLayouts = {};
        Object.entries(d.layouts).forEach(([bp, arr]) => {
          const bpSeen = new Set();
          cleanLayouts[bp] = (arr || []).filter((it) => {
            if (!it || bpSeen.has(it.i)) return false;
            bpSeen.add(it.i); return true;
          });
        });
        setLayouts(cleanLayouts);
      } else {
        setLayouts(DEFAULT_LAYOUTS);
      }
      setRemoved(d.removed || []);
      removedRef.current = d.removed || [];
      setNotes(d.notes || '');
      setReady(true);
    }).catch(() => setReady(true));
  }, [activeProfileId]);

  // AUTO-PLACEMENT RULE: every EXISTING list ALWAYS lands on the canvas.
  // But we also PRUNE orphan list-tiles (list-id no longer exists) AND
  // dedupe defensively. If pruning happens (data has changed), we PERSIST
  // the clean state so it doesn't reappear on next reload.
  useEffect(() => {
    if (!ready || !listsLoaded) return;
    setTiles((cur) => {
      const live = new Set(lists.map((l) => `list:${l.list_id}`));
      const seen = new Set();
      const pruned = cur.filter((id) => {
        if (typeof id !== 'string' || !id) return false;
        if (seen.has(id)) return false;
        // drop orphan list ids
        if (id.startsWith('list:') && !live.has(id)) return false;
        seen.add(id); return true;
      });
      const missing = [...live].filter((id) => !pruned.includes(id) && !removed.includes(id));
      if (!missing.length && pruned.length === cur.length) return cur;
      const nextLayouts = { ...layouts };
      BPS.forEach((bp) => {
        // Rebuild each breakpoint: dedupe, drop orphan-list layout rows, add
        // any newly-missing list rows at the bottom.
        const bpSeen = new Set();
        nextLayouts[bp] = [
          ...(nextLayouts[bp] || []).filter((it) => {
            if (!it || bpSeen.has(it.i)) return false;
            if (missing.includes(it.i)) return false;
            if (it.i.startsWith('list:') && !live.has(it.i)) return false;
            bpSeen.add(it.i); return true;
          }),
          ...missing.map((id) => ({ i: id, x: 0, y: 999, w: bp === 'sm' ? 2 : 4, h: 5 })),
        ];
      });
      const nextTiles = [...pruned, ...missing];
      setLayouts(nextLayouts);
      // PERSIST the cleaned state so tomorrow's load doesn't re-run this
      // prune (which is what was leaving ghost `list:` tiles on prod).
      persist(nextTiles, nextLayouts);
      return nextTiles;
    });
  }, [ready, listsLoaded, lists, removed]);

  useEffect(() => {
    const onInstall = (e) => { e.preventDefault(); setInstallEvt(e); };
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setCapOpen(true); setTimeout(() => capRef.current?.focus(), 60); }
      if (e.key === 'Escape') { setFocus(null); setCapOpen(false); setLibrary(false); setEditItem(null); setDelConfirm(null); setDrawer(null); }
    };
    window.addEventListener('beforeinstallprompt', onInstall);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('beforeinstallprompt', onInstall); window.removeEventListener('keydown', onKey); };
  }, []);

  const persist = useCallback((nextTiles, nextLayouts, nextRemoved, immediate) => {
    if (nextRemoved) removedRef.current = nextRemoved;
    clearTimeout(saveTimer.current);
    // Add/remove/library ops must save NOW so a fast navigate doesn't
    // drop the change (the old 700ms debounce lost writes on nav). Drag
    // /resize still debounces so we don't hammer the server per pixel.
    // Every write is scoped to the ACTIVE profile so ultrawide edits
    // don't leak into the phone layout and vice-versa.
    const body = { tiles: nextTiles, layouts: nextLayouts, removed: removedRef.current };
    const url = `/api/admin/desk/layout?profile=${encodeURIComponent(activeProfileId || 'default')}`;
    if (immediate) {
      req('PUT', url, body).catch(() => {});
      return;
    }
    saveTimer.current = setTimeout(() => {
      req('PUT', url, body).catch(() => {});
    }, 700);
  }, [activeProfileId]);

  // Flush pending saves on page hide (MJ navigating away mid-debounce).
  useEffect(() => {
    const onHide = () => {
      if (!saveTimer.current) return;
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
      // Best-effort: fire-and-forget, keepalive so it survives unload
      try {
        const url = `/api/admin/desk/layout?profile=${encodeURIComponent(activeProfileId || 'default')}`;
        const body = JSON.stringify({ tiles, layouts, removed: removedRef.current });
        fetch(`${window.location.origin}${url}`, {
          method: 'PUT', headers: auth(), body, keepalive: true,
        }).catch(() => {});
      } catch { /* noop */ }
    };
    window.addEventListener('pagehide', onHide);
    window.addEventListener('beforeunload', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('beforeunload', onHide);
    };
  }, [tiles, layouts]);

  const onLayoutChange = (_layout, allLayouts) => {
    setLayouts(allLayouts);
    persist(tiles, allLayouts);
  };
  const toggleArrange = () => { const n = !arrange; setArrange(n); arrangeRef.current = n; if (!n) persist(tiles, layouts, null, true); };
  const dragLock = () => document.body.classList.add('desk-dragging');
  const dragUnlock = () => { document.body.classList.remove('desk-dragging'); window.getSelection()?.removeAllRanges?.(); };
  const toggleDemo = () => setDemo((d) => { localStorage.setItem('desk-demo', d ? '' : '1'); return !d; });

  const addTile = (id, defOverride) => {
    if (tiles.includes(id)) return;
    const def = defOverride || tileDef(id, lists, board?.clients || []);
    const nextTiles = [...tiles, id];
    const nextLayouts = { ...layouts };
    BPS.forEach((bp) => {
      const item = { i: id, x: 0, y: 999, w: bp === 'sm' ? 2 : def.w, h: def.h };
      nextLayouts[bp] = [...(nextLayouts[bp] || []).filter((it) => it.i !== id), item];
    });
    const nextRemoved = removed.filter((x) => x !== id);
    setTiles(nextTiles); setLayouts(nextLayouts); setRemoved(nextRemoved);
    persist(nextTiles, nextLayouts, nextRemoved, true); // ← immediate save
    showToast(`＋ "${def.label}" tile added`);
  };
  const removeTile = (id) => {
    const nextTiles = tiles.filter((t) => t !== id);
    const nextLayouts = Object.fromEntries(Object.entries(layouts).map(([bp, l]) => [bp, l.filter((it) => it.i !== id)]));
    // Track EVERY removal in `removed` (not just list: prefixed) so no
    // automated seeding or auto-placement can silently reinject the tile.
    const nextRemoved = [...removed.filter((x) => x !== id), id];
    setTiles(nextTiles); setLayouts(nextLayouts); setRemoved(nextRemoved);
    persist(nextTiles, nextLayouts, nextRemoved, true); // ← immediate save
    showToast(`⤓ Tile returned to library — data untouched`, () => { addTile(id); });
  };

  // ── shared actions (carry-overs) ──────────────────────────────────────────
  const setState = async (it, state, label) => {
    const d = await req('PATCH', `/api/admin/desk/items/${it.item_id}`, { state });
    showToast(label || `→ ${state}`, async () => { await req('PATCH', `/api/admin/desk/items/${it.item_id}`, { state: d.prev_state }); load(); });
    load();
  };
  const doDelete = async (it) => {
    await req('DELETE', `/api/admin/desk/items/${it.item_id}?confirm=true`);
    setDelConfirm(null); setDelChain(null); showToast(`🗑 Deleted "${it.title}"`); load();
  };

  // When the confirm modal opens on a CLIENT, fetch the cascade preview
  // so MJ can see exactly what will be archived/deleted alongside it.
  useEffect(() => {
    setDelChain(null);
    if (!delConfirm || delConfirm.type !== 'client') return;
    let alive = true;
    req('GET', `/api/admin/desk/items/${delConfirm.item_id}/chain`)
      .then((c) => { if (alive) setDelChain(c); })
      .catch(() => {});
    return () => { alive = false; };
  }, [delConfirm?.item_id]);
  const saveEdit = async () => {
    await req('PATCH', `/api/admin/desk/items/${editItem.item_id}`, { title: editItem.title, body: editItem.body, value: editItem.value ? Number(editItem.value) : null, touch: true });
    setEditItem(null); showToast('✓ Saved'); load();
  };
  const composeFor = (leadId) => {
    if (leadId) localStorage.setItem('desk-compose-lead', leadId);
    window.location.assign('/admin/outreach?compose=1' + (leadId ? `&lead=${leadId}` : ''));
  };
  const pulseAction = (p) => {
    if (p.action_type === 'compose') composeFor(p.lead_id);
    else if (p.action_type === 'quote') window.location.assign(`/admin/os/quotes/${p.quote_id}`);
    else setFocus(p);
  };
  const enterFocus = (p) => setFocus(p);
  const demoteNow = async () => {
    const n = board?.now;
    if (!n) return;
    const payload = n.item_id ? { item_id: n.item_id }
      : { ref: n.kind === 'quote' ? `quote:${n.quote_id}` : n.kind === 'reply' ? `reply:${n.lead_id}` : `${n.kind}:${n.lead_id || ''}` };
    await req('POST', '/api/admin/desk/now/demote', payload);
    showToast('↓ Not now — snoozed 4h, next up promoted', async () => {
      await req('POST', '/api/admin/desk/now/demote', { ...payload, undo: true }); load();
    });
    load();
  };
  // Mark the Now card as DONE — triggers the win-sweep animation, promotes
  // the next pulse to the Now slot, and PERSISTS (desk_item flipped to
  // state='done' OR pulse-ref snoozed 30 days). Undo restores.
  const nowDone = async (evt) => {
    const n = board?.now;
    if (!n) return;
    const payload = n.item_id ? { item_id: n.item_id }
      : { ref: n.kind === 'quote' ? `quote:${n.quote_id}` : n.kind === 'reply' ? `reply:${n.lead_id}` : `${n.kind}:${n.lead_id || ''}` };
    try { sweep(evt); } catch (_) { /* motion optional */ }
    await req('POST', '/api/admin/desk/now/done', payload);
    showToast('✓ Done — swept to wins, next up promoted', async () => {
      await req('POST', '/api/admin/desk/now/done', { ...payload, undo: true }); load();
    });
    load();
  };
  const openDrawer = async () => setDrawer((await req('GET', '/api/admin/desk/archive')).items);
  const toggleMotion = () => { const next = !motion; setMotion(next); setMotionEnabled(next); };
  const installApp = async () => {
    if (installEvt) { installEvt.prompt(); const { outcome } = await installEvt.userChoice; if (outcome === 'accepted') { setInstallEvt(null); showToast('✓ Desk installed'); } }
    else showToast('iPhone/iPad: Share → Add to Home Screen · Desktop: install icon in the address bar');
  };

  // ── the tasteful firework: item flies to the Win Wall ────────────────────
  const sweep = useCallback((e) => {
    if (!motionEnabled() || reduced()) return;
    const from = e?.target?.getBoundingClientRect?.();
    const winEl = document.querySelector("[data-tile='wins']");
    const to = winEl?.getBoundingClientRect();
    if (!from || !to) return;
    const id = Math.random();
    setOrb({ id, x0: from.left + from.width / 2, y0: from.top + from.height / 2, x1: to.left + to.width / 2, y1: to.top + 20 });
    winEl.classList.add('desk-tile-winpulse');
    setTimeout(() => winEl.classList.remove('desk-tile-winpulse'), 1200);
    setTimeout(() => setOrb((o) => (o?.id === id ? null : o)), 950);
  }, []);
  const markDone = async (it, e) => { sweep(e); await setState(it, 'done', `🏆 "${it.title}" → Win Wall`); };

  // ── capture (FAB overlay + capture-pad tile share this) ──────────────────
  const capture = async (text, type) => {
    const d = await req('POST', '/api/admin/desk/omnibar', { text, ...(type ? { type } : {}) });
    if (d.list_created) {
      showToast(`${d.list_created.kind === 'project' ? '▦' : '☑'} "${d.list_created.name}" created — tile added`);
    } else {
      setConfirmChip({ items: d.created });
      setTimeout(() => setConfirmChip((c) => (c?.items === d.created ? null : c)), 9000);
    }
    load();
    return d;
  };
  const fabFile = async (type) => {
    const text = capVal.trim(); if (!text || capBusy) return;
    setCapBusy(true);
    try { await capture(text, type); setCapVal(''); } catch (e) { showToast('Capture failed: ' + e.message); }
    setCapBusy(false);
  };
  const undoCreate = async (items) => {
    for (const it of items) await req('DELETE', `/api/admin/desk/items/${it.item_id}?confirm=true`).catch(() => {});
    setConfirmChip(null); showToast('↩ Undone — nothing filed'); load();
  };
  const dictate = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { showToast('Voice not supported in this browser'); return; }
    const rec = new SR(); rec.lang = 'en-AU'; rec.interimResults = false;
    rec.onresult = (ev) => setCapVal((o) => (o + ' ' + ev.results[0][0].transcript).trim());
    rec.onend = () => setListening(false);
    setListening(true); rec.start();
  };
  const saveNotes = (text) => { setNotes(text); req('PUT', '/api/admin/desk/layout', { notes: text }).catch(() => {}); };
  const createListFromLibrary = async (kind = 'list') => {
    const name = newListName.trim(); if (!name) return;
    const d = await req('POST', '/api/admin/desk/lists', { name, kind });
    setNewListName('');
    await load();
    addTile(`list:${d.list.list_id}`, kind === 'project'
      ? { label: `▦ ${name}`, accent: VIOLET, w: 6, h: 6 }
      : { label: `☑ ${name}`, accent: TEAL, w: 4, h: 5 });
    setLibrary(false);
  };

  const dm = React.useMemo(() => {
    if (!demo) return (t) => t;
    const scrub = makeScrub(board?.clients || [], board?.mask_names || []);
    // privacy: debrief-sourced items carry real recorded-conversation content
    const debriefTitles = new Set();
    [...(board?.open_list || []), ...(board?.pulse || []), ...(board?.parking || []), ...(board?.wins || [])]
      .forEach((it) => { if (it?.source === 'debrief' && it.title) debriefTitles.add(it.title); });
    return (t) => {
      if (typeof t === 'string' && (debriefTitles.has(t) || t.startsWith('Promise:'))) return '⚡ Promise — hidden while presenting';
      return scrub(t);
    };
  }, [demo, board]);
  const openClient = (itemId) => window.location.assign(`/admin/clients/${itemId}`);
  const ctx = { board, req, showToast, load, setState, markDone, composeFor, pulseAction, demoteNow, nowDone, setEditItem, setDelConfirm, capture, anim, notes, saveNotes, sweep, demo, dm, openClient, enterFocus };

  const hotPulse = (board?.pulse || []).filter((p) => p.hot).length;
  const hotClients = (board?.clients || []).filter((c) => c.heat === 'hot').length;
  const badgeFor = (id) => (id === 'pulse' && hotPulse) || (id === 'clients' && hotClients) || 0;

  const renderTile = (id) => {
    if (id === 'now') return <NowTile ctx={ctx} />;
    if (id === 'pulse') return <PulseTile ctx={ctx} />;
    if (id === 'clients') return <ClientsTile ctx={ctx} />;
    if (id === 'money') return <MoneyTile ctx={ctx} />;
    if (id === 'parking') return <ParkingTile ctx={ctx} />;
    if (id === 'wins') return <WinsTile ctx={ctx} />;
    if (id === 'ai') return <DeskAITile key={demo ? 'demo' : 'live'} ctx={ctx} />;
    if (id === 'capture') return <CapturePadTile ctx={ctx} />;
    if (id === 'notes') return <NotesTile ctx={ctx} />;
    if (id === 'clock') return <ClockTile />;
    if (id === 'winsweek') return <WinsWeekTile ctx={ctx} />;
    if (id === 'sendstoday') return <SendsTodayTile ctx={ctx} />;
    if (id === 'invoices') return <UnpaidInvoicesTile ctx={ctx} />;
    if (id === 'enquiries') return <EnquiriesTile ctx={ctx} />;
    if (id === 'awaiting') return <AwaitingReplyTile ctx={ctx} />;
    if (id === 'promises') return <PromisesTile ctx={ctx} />;
    if (id === 'calendar') return <CalendarTile ctx={ctx} />;
    if (id === 'payments') return <PaymentsTile ctx={ctx} />;
    if (id === 'inbox') return <InboxTile ctx={ctx} />;
    if (id === 'pipeline') return <PipelineTile ctx={ctx} />;
    if (id === 'quotepipeline') return <QuotePipelineTile ctx={ctx} />;
    if (id === 'fittingbay') return <FittingBayTile ctx={ctx} />;
    if (id === 'gilliland') return <GillilandWorldTile ctx={ctx} />;
    if (id === 'quotesaging') return <QuotesAgingTile ctx={ctx} />;
    if (id === 'projects') return <ProjectsTile ctx={ctx} />;
    if (id === 'llmbudget') return <LLMBudgetTile ctx={ctx} />;
    if (id === 'sweepaudit') return <SweepAuditTile ctx={ctx} />;
    if (id === 'jobs') return <JobsTile ctx={ctx} />;
    if (id === 'focustimer') return <FocusTimerTile ctx={ctx} />;
    if (id === 'quicklinks') return <QuickLinksTile ctx={ctx} />;
    if (id === 'weather') return <WeatherTile ctx={ctx} />;
    if (id === 'debrief') return <DebriefTile ctx={ctx} onOpen={(init) => !demo && setDebriefOpen(init && init.person ? init : true)} />;
    if (id === 'actions') return <TodayActionsTile ctx={ctx} />;
    if (id === 'wonchart') return <WonChartTile ctx={ctx} />;
    if (id === 'repliestrend') return <RepliesTrendTile ctx={ctx} />;
    if (id === 'moneyxero') return <MoneyXeroTile ctx={ctx} />;
    if (id === 'moneyxerodetail') return <MoneyXeroDetailTile ctx={ctx} />;
    if (id === 'ghlenquiries') return <GhlEnquiriesTile ctx={ctx} />;
    if (id === 'ghlcampaigns') return <GhlCampaignsTile ctx={ctx} />;
    if (id === 'ghlpipeline') return <GhlPipelineTile ctx={ctx} />;
    if (id === 'wfmbay') return <WfmBayTile ctx={ctx} />;
    if (id === 'moneypipelines') return <MoneyPipelinesTile ctx={ctx} />;
    if (id.startsWith('list:')) {
      const l = lists.find((x) => x.list_id === id.slice(5));
      return <ListTile listId={id.slice(5)} kind={l?.kind || 'list'} req={req} showToast={showToast} onChanged={load} tick={tick} sweep={sweep} />;
    }
    if (id.startsWith('client:')) return <SingleClientTile ctx={ctx} itemId={id.slice(7)} />;
    return null;
  };

  const availableTiles = [
    ...Object.keys(STATIC_TILES).filter((id) => !tiles.includes(id)).map((id) => ({ id, ...STATIC_TILES[id] })),
    ...lists.filter((l) => !tiles.includes(`list:${l.list_id}`)).map((l) => ({ id: `list:${l.list_id}`, label: `☑ ${l.name}`, accent: TEAL })),
    ...(board?.clients || []).filter((c) => !tiles.includes(`client:${c.item_id}`)).map((c) => ({ id: `client:${c.item_id}`, label: `👤 ${c.title}`, accent: AMBER })),
  ];

  if (loadErr && !board) return (
    <div data-testid="desk-fallback" style={{ maxWidth: 560, margin: '80px auto', padding: '28px 30px', textAlign: 'center', background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 14 }}>
      <h2 style={{ fontFamily: HEAD, fontSize: 20, fontWeight: 800, color: TXT, margin: '0 0 6px' }}>Couldn't load the Desk</h2>
      <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, color: SUB, margin: '0 0 16px' }}>{loadErr} — the rest of the admin is untouched.</p>
      <button data-testid="desk-fallback-retry" onClick={load} style={{ ...mini, color: TEAL, borderColor: 'rgba(84,209,151,0.4)', marginRight: 8 }}>↻ Try again</button>
      <a href="/admin/outreach" data-testid="desk-fallback-outreach" style={{ ...mini, textDecoration: 'none' }}>✉ Go to Outreach</a>
    </div>
  );

  return (
    <div data-testid="desk-page" style={{ position: 'relative', minHeight: 'calc(100vh - 60px)', overflow: 'hidden' }}>
      <style>{`
        @keyframes desk-draw { from { stroke-dashoffset: 200; } to { stroke-dashoffset: 0; } }
        @keyframes desk-pop { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        @keyframes desk-win { 0% { transform: scale(1); } 35% { transform: scale(1.04); background: rgba(84,209,151,0.22); box-shadow: 0 0 22px rgba(84,209,151,0.5); } 100% { transform: scale(1); opacity: 0.4; } }
        .desk-win-pop { animation: desk-win 700ms ${EASE.out}; }
        .desk-crossoff { text-decoration: line-through; opacity: 0.45; transition: opacity 500ms ease; }
        @keyframes lib-shimmer { 0% { background-position: 0% 0; } 100% { background-position: 200% 0; } }
        .lib-accent { animation: lib-shimmer 6s linear infinite; }
        @keyframes desk-sheen { 0% { transform: translateX(-120%); } 100% { transform: translateX(320%); } }
        .desk-accent-sheen { position: absolute; top: 0; bottom: 0; width: 34%; pointer-events: none;
          background: linear-gradient(105deg, transparent, rgba(236,124,36,0.07), rgba(84,209,151,0.05), transparent);
          animation: desk-sheen 9s ${EASE.out} infinite; }
        .desk-heat-hot { box-shadow: 0 0 16px rgba(236,124,36,0.28), inset 0 0 0 1px rgba(236,124,36,0.35) !important; }
        @keyframes desk-aurora-a { 0% { transform: translate(-8%, -6%) rotate(0deg); } 100% { transform: translate(6%, 8%) rotate(28deg); } }
        @keyframes desk-aurora-b { 0% { transform: translate(10%, 6%) rotate(0deg); } 100% { transform: translate(-6%, -10%) rotate(-24deg); } }
        .desk-aurora::before, .desk-aurora::after { content: ''; position: absolute; width: 60vw; height: 60vw; border-radius: 50%; filter: blur(90px); opacity: 0.5; }
        .desk-aurora::before { top: -18vw; left: -10vw; background: radial-gradient(circle, rgba(236,124,36,0.5), rgba(84,209,151,0.14) 55%, transparent 70%); animation: desk-aurora-a 48s ease-in-out infinite alternate; }
        .desk-aurora::after { bottom: -22vw; right: -12vw; background: radial-gradient(circle, rgba(236,124,36,0.34), rgba(123,176,236,0.14) 55%, transparent 70%); animation: desk-aurora-b 56s ease-in-out infinite alternate; }
        html[data-motion="off"] .desk-aurora::before, html[data-motion="off"] .desk-aurora::after { animation: none; }
        @media (prefers-reduced-motion: reduce) { .desk-aurora::before, .desk-aurora::after { animation: none !important; } }
        .desk-tile { background: linear-gradient(158deg, rgba(255,255,255,0.085), rgba(255,255,255,0.022)); border: 1px solid rgba(255,255,255,0.10); border-radius: 12px; backdrop-filter: blur(26px) saturate(175%); -webkit-backdrop-filter: blur(26px) saturate(175%); box-shadow: 0 10px 30px -14px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.16); display: flex; flex-direction: column; overflow: hidden; transition: box-shadow ${DUR.base}ms ${EASE.out}, border-color ${DUR.base}ms ${EASE.out}; }
        .desk-tile:hover { box-shadow: 0 14px 40px -12px rgba(0,0,0,0.5), 0 0 0 1px rgba(236,124,36,0.28), inset 0 1px 0 rgba(255,255,255,0.16); }
        /* Tile remove ✕ is ALWAYS in the DOM — visible on tile-hover
           (so MJ never has to enter arrange mode just to delete one),
           and always-on inside arrange mode. Touch devices show it
           permanently since there's no hover on touch. */
        .desk-tile:hover .tile-remove-btn, .desk-arrange .tile-remove-btn { opacity: 1 !important; }
        @media (hover: none) { .tile-remove-btn { opacity: 1 !important; } }
        .desk-arrange, .desk-arrange * { user-select: none !important; -webkit-user-select: none !important; }
        .desk-arrange input, .desk-arrange textarea { user-select: text !important; -webkit-user-select: text !important; }
        .tile-drag-handle { user-select: none; -webkit-user-select: none; cursor: grab; touch-action: none; }
        .tile-drag-handle:active { cursor: grabbing; }
        body.desk-dragging, body.desk-dragging * { user-select: none !important; -webkit-user-select: none !important; cursor: grabbing !important; }
        .desk-arrange .desk-tile { cursor: default; box-shadow: 0 8px 26px rgba(0,0,0,0.35); border-color: rgba(236,124,36,0.55); animation: desk-jiggle 2.6s ease-in-out infinite; }
        @keyframes desk-jiggle { 0%,100% { rotate: 0deg; } 25% { rotate: 0.2deg; } 75% { rotate: -0.2deg; } }
        html[data-motion="off"] .desk-arrange .desk-tile { animation: none; }
        .desk-arrange .tile-drag-handle { background: rgba(236,124,36,0.12); border-radius: 12px 12px 0 0; }
        .desk-arrange .react-resizable-handle, .react-grid-item:hover .react-resizable-handle { display: block; }
        .react-resizable-handle { display: none; position: absolute; width: 20px; height: 20px; z-index: 5; background-image: none; }
        .react-resizable-handle-se { right: 2px; bottom: 2px; cursor: se-resize; border-right: 3px solid rgba(236,124,36,0.95); border-bottom: 3px solid rgba(236,124,36,0.95); border-radius: 0 0 10px 0; }
        .react-grid-item.react-draggable-dragging .desk-tile, .react-grid-item.dragging .desk-tile { transform: scale(1.02) rotate(0.8deg); box-shadow: 0 22px 50px rgba(0,0,0,0.55), 0 0 30px rgba(236,124,36,0.35); }
        .react-grid-item { transition: transform 320ms cubic-bezier(0.34, 1.4, 0.64, 1), width 320ms ${EASE.out}, height 320ms ${EASE.out}; }
        .react-grid-item.react-draggable-dragging, .react-grid-item.resizing { transition: none; z-index: 30; }
        .react-grid-placeholder, .react-grid-item.react-grid-placeholder { background: rgba(236,124,36,0.22) !important; border-radius: 12px; border: 1px dashed rgba(236,124,36,0.6); }
        html[data-motion="off"] .react-grid-item { transition: none; }
        @keyframes desk-winpulse { 0%,100% { box-shadow: 0 0 0 rgba(84,209,151,0); } 40% { box-shadow: 0 0 44px rgba(84,209,151,0.55); } }
        .desk-tile-winpulse .desk-tile { animation: desk-winpulse 1.1s ease-out; }
        @keyframes desk-orb-fly { to { transform: translate(var(--dx), var(--dy)) scale(0.4); opacity: 0.1; } }
        @keyframes desk-orb-breathe { 0%,100% { transform: scale(1); opacity: 0.9; } 50% { transform: scale(1.18); opacity: 1; } }
        .desk-orb-breathe { animation: desk-orb-breathe 3.4s ease-in-out infinite; }
        @keyframes desk-sweepline { from { transform: translateX(-110%); } to { transform: translateX(110vw); } }
        html[data-motion="off"] .desk-orb-breathe { animation: none; }
      `}</style>

      {/* Expedition backdrop: charcoal + orange glows, faint topographic
          contour motif, and a soft top ridge — the TravelTrucks "Aero" base. */}
      <div data-testid="desk-backdrop" style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', background: `radial-gradient(1300px 760px at 80% -12%, rgba(236,124,36,0.24), transparent 56%), radial-gradient(900px 600px at 8% 6%, rgba(236,124,36,0.08), transparent 50%), radial-gradient(1200px 900px at 55% 128%, rgba(90,104,128,0.14), transparent 60%), linear-gradient(180deg,#101113 0%, #0B0C0E 60%, #090A0B 100%)` }} />
      <div data-testid="desk-topo" style={{ position: 'absolute', inset: 0, zIndex: 0, opacity: 0.05, pointerEvents: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='700' height='700'%3E%3Cg fill='none' stroke='%23EC7C24' stroke-width='1'%3E%3Cpath d='M-50 200 Q175 120 350 200 T750 200'/%3E%3Cpath d='M-50 260 Q175 180 350 260 T750 260'/%3E%3Cpath d='M-50 320 Q175 240 350 320 T750 320'/%3E%3Cpath d='M-50 380 Q175 300 350 380 T750 380'/%3E%3Cpath d='M-50 440 Q175 360 350 440 T750 440'/%3E%3Cpath d='M-50 500 Q175 420 350 500 T750 500'/%3E%3C/g%3E%3C/svg%3E")` }} />
      {/* slow-drifting orange aurora over the backdrop */}
      <div className="desk-aurora" data-testid="desk-aurora" style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }} />

      <div style={{ position: 'relative', zIndex: 1, padding: '10px 14px 90px' }}>
        {/* desk header row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '4px 6px 10px' }}>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontFamily: HEAD, fontSize: 19, fontWeight: 800, color: TXT, margin: 0 }}>
              The <span style={{ background: SPEC, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Desk</span>
            </h1>
            <p data-testid="desk-greeting" style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5, color: SUB }}>
              {new Date().getHours() < 12 ? 'Morning, Col — your rig, your call.' : new Date().getHours() < 17 ? 'Arvo, Col — your rig, your call.' : 'Evening, Col — your rig, your call.'}
            </p>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap' }}>
            <ProfileSwitcher profile={profile} />
            <button data-testid="desk-arrange-toggle" onClick={toggleArrange}
              style={{ ...mini, ...(arrange ? { color: '#FFFFFF', background: SPEC, border: 'none', fontWeight: 800 } : { color: VIOLET, borderColor: 'rgba(236,124,36,0.5)' }) }}>
              ✦ {arrange ? 'Arranging — tap to lock' : 'Arrange'}
            </button>
            <button data-testid="desk-demo-toggle" onClick={toggleDemo}
              style={{ ...mini, ...(demo ? { color: '#0E0F11', background: AMBER, border: 'none', fontWeight: 800 } : { color: AMBER, borderColor: 'rgba(247,150,64,0.45)' }) }}>
              🎭 {demo ? 'Presenting' : 'Demo'}
            </button>
            <button data-testid="desk-add-tile-btn" onClick={() => setLibrary(true)} style={{ ...mini, color: TEAL, borderColor: 'rgba(84,209,151,0.4)' }}>＋ Add tile</button>
            {!standalone && <button data-testid="desk-install-btn" onClick={installApp} style={mini}>⤓ Install</button>}
            <button data-testid="desk-archive-drawer-btn" onClick={openDrawer} style={mini}>⤓ Archive</button>
            <button data-testid="desk-motion-toggle" onClick={toggleMotion} style={{ ...mini, color: motion ? TEAL : SUB }}>{motion ? '✦ Motion' : '· Still'}</button>
          </div>
        </div>

        {/* THE TILE SANDBOX */}
        <div className={arrange ? 'desk-arrange' : ''} data-testid="desk-canvas">
          {ready && (
            <ResponsiveGrid
              className="layout"
              layouts={layouts}
              breakpoints={{ xl: 2000, lg: 1100, md: 700, sm: 0 }}
              cols={{ xl: 18, lg: 12, md: 8, sm: 2 }}
              rowHeight={38}
              margin={[14, 14]}
              isDraggable
              isResizable
              draggableHandle=".tile-drag-handle"
              draggableCancel=".no-drag"
              resizeHandles={['se']}
              compactType="vertical"
              onLayoutChange={onLayoutChange}
              onDragStart={dragLock}
              onDragStop={dragUnlock}
              onResizeStart={dragLock}
              onResizeStop={dragUnlock}
            >
              {tiles.map((id) => {
                const def = tileDef(id, lists, board?.clients || []);
                const badge = badgeFor(id);
                return (
                  <div key={id} data-tile={id}>
                    <div className="desk-tile" style={{ height: '100%' }}>
                      <div className="tile-drag-handle" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px 6px', flexShrink: 0 }}>
                        <span className="tile-grip" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: arrange ? VIOLET : SUB, letterSpacing: '1px', flexShrink: 0, opacity: arrange ? 1 : 0.5 }}>⠿</span>
                        <SourceMark source={sourceFor(id)} size={18} testId={`tile-source-${id}`} />
                        <p style={{ margin: 0, flex: 1, fontFamily: 'Source Sans 3, sans-serif', fontSize: 8.5, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: def.accent, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{dm(def.label)}</p>
                        {badge > 0 && <span data-testid={`tile-badge-${id}`} style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9, fontWeight: 800, color: '#0E0F11', background: MAGENTA, borderRadius: 999, padding: '0 6px' }}>{badge}</span>}
                        <button data-testid={`tile-remove-${id}`} className="no-drag tile-remove-btn" onClick={() => removeTile(id)} title="Remove this tile · returns to widget library · data untouched"
                          style={{ width: 20, height: 20, borderRadius: '50%', display: 'grid', placeItems: 'center', background: arrange ? 'rgba(236,124,36,0.18)' : 'rgba(236,124,36,0.08)', border: `1px solid ${arrange ? MAGENTA : 'rgba(236,124,36,0.35)'}`, color: MAGENTA, cursor: 'pointer', fontSize: 11, fontWeight: 800, padding: 0, flexShrink: 0, opacity: arrange ? 1 : 0, transition: 'opacity 160ms ease' }}>✕</button>
                      </div>
                      <div className="studio-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '2px 12px 12px' }}>
                        <TileBoundary id={id}><CrashProbe id={id} />{renderTile(id)}</TileBoundary>
                      </div>
                    </div>
                  </div>
                );
              })}
            </ResponsiveGrid>
          )}
        </div>
      </div>

      {/* PRESENTING indicator */}
      {demo && (
        <div data-testid="desk-presenting-banner" style={{ position: 'fixed', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 90, fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5, fontWeight: 800, letterSpacing: '0.18em', color: '#0E0F11', background: AMBER, borderRadius: 999, padding: '5px 16px', boxShadow: '0 6px 20px rgba(247,150,64,0.45)', pointerEvents: 'none' }}>
          🎭 PRESENTING — DETAILS MASKED
        </div>
      )}

      {/* flying win orb */}
      {orb && (
        <span key={orb.id} data-testid="win-orb" style={{ position: 'fixed', left: orb.x0, top: orb.y0, zIndex: 85, width: 16, height: 16, borderRadius: '50%', background: SPEC, boxShadow: '0 0 18px rgba(84,209,151,0.8)', pointerEvents: 'none', '--dx': `${orb.x1 - orb.x0}px`, '--dy': `${orb.y1 - orb.y0}px`, animation: 'desk-orb-fly 850ms cubic-bezier(0.5, -0.2, 0.3, 1) forwards' }} />
      )}

      {/* FAB — capture is never more than one tap away */}
      <button data-testid="desk-fab" onClick={() => { setCapOpen(true); setTimeout(() => capRef.current?.focus(), 60); }}
        style={{ position: 'fixed', right: 22, bottom: 22, zIndex: 75, width: 56, height: 56, borderRadius: '50%', border: 'none', background: SPEC, color: '#FFFFFF', fontSize: 24, fontWeight: 800, cursor: 'pointer', boxShadow: '0 10px 30px rgba(236,124,36,0.5)' }}>＋</button>

      {/* CAPTURE OVERLAY */}
      {capOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 76, background: 'rgba(8,9,11,0.72)', backdropFilter: 'blur(4px)' }} onClick={() => setCapOpen(false)}>
          <div data-testid="capture-overlay" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640, margin: '12vh auto 0', background: '#17181B', border: '1px solid transparent', backgroundImage: `linear-gradient(#17181B, #17181B), ${SPEC}`, backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box', borderRadius: 18, padding: 18, animation: anim ? 'desk-pop 220ms ease-out' : 'none' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <input ref={capRef} data-testid="omnibar-input" placeholder='Dump anything — "met a plumber at bunnings, call thursday"' value={capVal}
                onChange={(e) => setCapVal(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fabFile()} disabled={capBusy}
                style={{ flex: 1, minWidth: 0, background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 11, padding: '11px 14px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 13.5, color: TXT, outline: 'none' }} />
              <button data-testid="omnibar-mic" onClick={dictate} style={{ ...mini, padding: '0 13px', color: listening ? MAGENTA : TXT }}>{listening ? '● rec' : '🎙'}</button>
              <button data-testid="omnibar-file" onClick={() => fabFile()} disabled={capBusy || !capVal.trim()}
                style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, fontWeight: 800, color: '#FFFFFF', background: SPEC, border: 'none', borderRadius: 11, padding: '0 18px', cursor: 'pointer', opacity: capBusy || !capVal.trim() ? 0.5 : 1 }}>{capBusy ? '…' : 'File it'}</button>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              {Object.entries(TYPE_META).map(([t, [ic]]) => (
                <button key={t} data-testid={`omnibar-chip-${t}`} onClick={() => fabFile(t)} disabled={capBusy || !capVal.trim()}
                  style={{ ...mini, padding: '3px 10px', fontSize: 10.5, opacity: !capVal.trim() ? 0.45 : 1 }}>{ic} +{t[0].toUpperCase() + t.slice(1)}</button>
              ))}
              <span style={{ marginLeft: 'auto', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, color: SUB }}>Esc to close · ⌘K anywhere</span>
            </div>
            {confirmChip && (
              <div data-testid="omnibar-confirm-chip" style={{ display: 'inline-flex', gap: 7, alignItems: 'center', marginTop: 10, fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, fontWeight: 700, color: TEAL, border: `1px solid rgba(84,209,151,0.45)`, borderRadius: 999, padding: '4px 12px', animation: anim ? 'desk-pop 250ms ease-out' : 'none' }}>
                ✓ Filed: {confirmChip.items.map((i) => `${TYPE_META[i.type]?.[0] || ''} ${i.title}`).join(' + ')}
                <button data-testid="omnibar-undo" onClick={() => undoCreate(confirmChip.items)} style={{ ...mini, padding: '1px 9px', fontSize: 10 }}>Undo</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TILE LIBRARY DRAWER */}
      {library && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 74, background: 'rgba(8,9,11,0.6)' }} onClick={() => setLibrary(false)}>
          <div data-testid="tile-library" className="studio-scroll" onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 320, maxWidth: '88%', background: '#141518', borderLeft: `1px solid ${BORDER}`, padding: 18, overflowY: 'auto' }}>
            <p style={{ margin: '0 0 4px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 9, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: SUB }}>Widget library</p>
            <p style={{ margin: '0 0 12px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5, color: SUB }}>Every widget is a normal tile — drag, resize, remove. Removing never deletes data.</p>
            {LIB_CATS.map(([cat, icon, name]) => {
              const inCat = availableTiles.filter((t) => !t.id.startsWith('list:') && (WIDGET_META[t.id] || [t.id.startsWith('client:') ? 'clients' : 'work'])[0] === cat);
              if (!inCat.length) return null;
              return (
                <div key={cat} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span className="lib-orb" style={{ width: 26, height: 26, borderRadius: 9, display: 'grid', placeItems: 'center', fontSize: 13, background: 'rgba(236,124,36,0.16)', border: `1px solid ${BORDER}` }}>{icon}</span>
                    <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: TXT }}>{name}</span>
                    <span className="lib-accent" style={{ flex: 1, height: 2, borderRadius: 2, background: SPEC, backgroundSize: '200% 100%', opacity: 0.5 }} />
                  </div>
                  {inCat.map((t) => (
                    <button key={t.id} data-testid={`library-add-${t.id}`} onClick={() => { addTile(t.id); }}
                      style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 9, padding: '8px 10px', margin: '4px 0', borderRadius: 11, background: 'rgba(255,255,255,0.04)', border: `1px solid ${BORDER}`, cursor: 'pointer', textAlign: 'left' }}>
                      <span style={{ width: 34, height: 26, borderRadius: 7, flexShrink: 0, display: 'grid', placeItems: 'center', fontSize: 11, background: `linear-gradient(135deg, rgba(236,124,36,0.25), rgba(84,209,151,0.12))`, border: `1px solid ${BORDER}` }}>{t.label.match(/^\p{Emoji}/u)?.[0] || '▢'}</span>
                      <span style={{ minWidth: 0, flex: 1 }}>
                        <span style={{ display: 'block', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, fontWeight: 700, color: TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{dm(t.label)}</span>
                        {WIDGET_META[t.id] && <span style={{ display: 'block', fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, color: SUB }}>{WIDGET_META[t.id][1]}</span>}
                      </span>
                      <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, fontWeight: 800, color: t.accent, flexShrink: 0 }}>＋</span>
                    </button>
                  ))}
                </div>
              );
            })}
            {availableTiles.filter((t) => t.id.startsWith('list:')).length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span className="lib-orb" style={{ width: 26, height: 26, borderRadius: 9, display: 'grid', placeItems: 'center', fontSize: 13, background: 'rgba(84,209,151,0.14)', border: `1px solid ${BORDER}` }}>☑</span>
                  <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: TXT }}>Lists & projects</span>
                  <span className="lib-accent" style={{ flex: 1, height: 2, borderRadius: 2, background: SPEC, backgroundSize: '200% 100%', opacity: 0.5 }} />
                </div>
                {availableTiles.filter((t) => t.id.startsWith('list:')).map((t) => (
                  <button key={t.id} data-testid={`library-add-${t.id}`} onClick={() => { addTile(t.id); }}
                    style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 8, padding: '8px 10px', margin: '4px 0', borderRadius: 11, background: 'rgba(255,255,255,0.04)', border: `1px solid ${BORDER}`, cursor: 'pointer', textAlign: 'left' }}>
                    <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, fontWeight: 700, color: TXT, flex: 1 }}>{dm(t.label)}</span>
                    <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, fontWeight: 800, color: t.accent }}>＋</span>
                  </button>
                ))}
              </div>
            )}
            {availableTiles.length === 0 && <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, color: SUB }}>Everything's on the board.</p>}
            <div style={{ marginTop: 4, marginBottom: 10 }}>
              <p style={{ margin: '0 0 5px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 9, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: SUB }}>🔜 Coming soon</p>
              {COMING_SOON.map((l) => (
                <div key={l} data-testid="library-coming-soon" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', margin: '4px 0', borderRadius: 11, background: 'rgba(255,255,255,0.02)', border: `1px dashed ${BORDER}`, opacity: 0.45 }}>
                  <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, fontWeight: 600, color: SUB, flex: 1 }}>{l}</span>
                  <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 8.5, fontWeight: 800, letterSpacing: '0.1em', color: SUB }}>SOON</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 14, borderTop: `1px solid ${BORDER}`, paddingTop: 12 }}>
              <p style={{ margin: '0 0 6px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 9, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: TEAL }}>＋ New list / project → tile</p>
              <input data-testid="library-new-list-input" placeholder="Name it…" value={newListName} onChange={(e) => setNewListName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && createListFromLibrary('list')}
                style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '8px 11px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: TXT, outline: 'none' }} />
              <div style={{ display: 'flex', gap: 6, marginTop: 7 }}>
                <button data-testid="library-new-list-save" onClick={() => createListFromLibrary('list')} disabled={!newListName.trim()}
                  style={{ ...mini, flex: 1, color: TEAL, borderColor: 'rgba(84,209,151,0.4)', opacity: newListName.trim() ? 1 : 0.5 }}>☑ Create list</button>
                <button data-testid="library-new-project-save" onClick={() => createListFromLibrary('project')} disabled={!newListName.trim()}
                  style={{ ...mini, flex: 1, color: VIOLET, borderColor: 'rgba(236,124,36,0.5)', opacity: newListName.trim() ? 1 : 0.5 }}>▦ Create project</button>
              </div>
              <p style={{ margin: '7px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, color: SUB }}>Project = board tile with To do / Doing / Done columns.</p>
            </div>
          </div>
        </div>
      )}

      {/* FOCUS MODE */}
      {debriefOpen && !demo && <DebriefView ctx={ctx} initial={debriefOpen === true ? null : debriefOpen} onClose={() => setDebriefOpen(false)} />}
      {focus && (
        <div data-testid="desk-focus-overlay" onClick={() => setFocus(null)} style={{ position: 'fixed', inset: 0, zIndex: 78, background: 'rgba(8,9,11,0.9)', backdropFilter: 'blur(6px)', display: 'grid', placeItems: 'center' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520, width: '90%', padding: '30px 32px', textAlign: 'center', borderRadius: 16, border: '1px solid transparent', backgroundImage: `linear-gradient(${BG}, ${BG}), ${SPEC}`, backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box' }}>
            <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 9, fontWeight: 800, letterSpacing: '0.22em', textTransform: 'uppercase', color: TEAL }}>FOCUS — just this</p>
            <h2 style={{ fontFamily: HEAD, fontSize: 24, fontWeight: 800, color: TXT, margin: '10px 0 6px' }}>{dm(focus.title)}</h2>
            <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, color: SUB, margin: '0 0 18px' }}>{dm(focus.sub)} · Esc to surface</p>
            <FocusRing key={focus.title} showToast={showToast} />

            {focus.item_id && (
              <button data-testid="focus-done" onClick={async (e) => { sweep(e); await req('PATCH', `/api/admin/desk/items/${focus.item_id}`, { state: 'done' }); setFocus(null); showToast('🏆 Done → Win Wall'); load(); }}
                style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, fontWeight: 800, color: '#FFFFFF', background: SPEC, border: 'none', borderRadius: 12, padding: '11px 24px', cursor: 'pointer' }}>✓ Done — to the Win Wall</button>
            )}
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {editItem && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 78, background: 'rgba(8,9,11,0.8)', display: 'grid', placeItems: 'center' }} onClick={() => setEditItem(null)}>
          <div data-testid="desk-edit-modal" onClick={(e) => e.stopPropagation()} style={{ width: 440, maxWidth: '92%', padding: 20, background: '#17181B', border: `1px solid ${BORDER}`, borderRadius: 16 }}>
            <p style={{ margin: '0 0 10px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 9, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: SUB }}>Edit {editItem.type}</p>
            <input data-testid="edit-title" value={editItem.title} onChange={(e) => setEditItem({ ...editItem, title: e.target.value })}
              style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '9px 12px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, color: TXT, marginBottom: 8, outline: 'none' }} />
            <textarea data-testid="edit-body" rows={3} value={editItem.body || ''} onChange={(e) => setEditItem({ ...editItem, body: e.target.value })}
              style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '9px 12px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, color: TXT, marginBottom: 8, outline: 'none', resize: 'vertical' }} />
            <input data-testid="edit-value" placeholder="$ value (optional)" value={editItem.value || ''} onChange={(e) => setEditItem({ ...editItem, value: e.target.value })}
              style={{ width: 140, background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '9px 12px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, color: TXT, outline: 'none' }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditItem(null)} style={mini}>Cancel</button>
              <button data-testid="edit-save" onClick={saveEdit} style={{ ...mini, color: TEAL, borderColor: 'rgba(84,209,151,0.45)' }}>✓ Save</button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {delConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 78, background: 'rgba(8,9,11,0.8)', display: 'grid', placeItems: 'center' }} onClick={() => { setDelConfirm(null); setDelChain(null); }}>
          <div data-testid="desk-delete-confirm" onClick={(e) => e.stopPropagation()} style={{ width: 420, maxWidth: '92%', padding: 20, textAlign: 'center', background: '#17181B', border: `1px solid ${BORDER}`, borderRadius: 16 }}>
            <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 13.5, fontWeight: 700, color: TXT, margin: '0 0 4px' }}>{delConfirm.state === 'archived' ? `Delete "${dm(delConfirm.title)}" forever?` : `Archive "${dm(delConfirm.title)}"?`}</p>
            <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, color: SUB, margin: '0 0 12px' }}>{delConfirm.state === 'archived' ? 'This cannot be undone. Archive is usually the better move — it keeps the history.' : 'Archive is the safe default — restorable from the Archive drawer.'}</p>
            {delConfirm.type === 'client' && delChain && (delChain.quotes.length > 0 || delChain.jobs.length > 0) && (
              <div data-testid="desk-delete-chain-preview" style={{ margin: '0 0 12px', padding: '10px 12px', borderRadius: 10, background: 'rgba(247,150,64,0.08)', border: '1px solid rgba(247,150,64,0.28)', textAlign: 'left' }}>
                <p style={{ margin: '0 0 6px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#F79640' }}>{delConfirm.state === 'archived' ? '⚠ Also deleted' : '⚠ Also archived (chain-linked)'}</p>
                {delChain.quotes.map((q) => (
                  <p key={q.quote_id} data-testid={`chain-quote-${q.quote_id}`} style={{ margin: '2px 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: TXT }}>
                    · Quote {q.quote_number || q.quote_id.slice(0, 8)} — <b style={{ color: '#F79640' }}>${(q.total_inc || 0).toLocaleString()}</b> <span style={{ color: SUB }}>({q.status}{q.archived ? ' · already archived' : ''})</span>
                  </p>
                ))}
                {delChain.jobs.map((j) => (
                  <p key={j.job_id} data-testid={`chain-job-${j.job_id}`} style={{ margin: '2px 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: TXT }}>
                    · Job {j.quote_number || j.job_id.slice(0, 8)} — <b style={{ color: '#F79640' }}>${(j.value || 0).toLocaleString()}</b> <span style={{ color: SUB }}>({j.stage}{j.archived ? ' · already archived' : ''})</span>
                  </p>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button data-testid="delete-cancel" onClick={() => { setDelConfirm(null); setDelChain(null); }} style={mini}>Cancel</button>
              {delConfirm.state !== 'archived' && (
                <button data-testid="delete-archive-instead" onClick={() => { setState(delConfirm, 'archived', `⤓ Archived "${delConfirm.title}"${delChain && (delChain.quotes.length || delChain.jobs.length) ? ` +${delChain.quotes.length} quote${delChain.quotes.length !== 1 ? 's' : ''}, +${delChain.jobs.length} job${delChain.jobs.length !== 1 ? 's' : ''}` : ''}`); setDelConfirm(null); setDelChain(null); }} style={{ ...mini, color: AMBER, borderColor: 'rgba(247,150,64,0.45)' }}>⤓ Archive instead</button>
              )}
              <button data-testid="delete-confirm-btn" onClick={() => doDelete(delConfirm)} style={{ ...mini, color: MAGENTA, borderColor: 'rgba(236,124,36,0.5)' }}>🗑 Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ARCHIVE DRAWER */}
      {drawer && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 74, background: 'rgba(8,9,11,0.6)' }} onClick={() => setDrawer(null)}>
          <div data-testid="desk-archive-drawer" className="studio-scroll" onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 340, maxWidth: '88%', background: '#141518', borderLeft: `1px solid ${BORDER}`, padding: 18, overflowY: 'auto' }}>
            <p style={{ margin: '0 0 12px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 9, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: SUB }}>⤓ Archive — restore in one tap</p>
            {drawer.map((i) => (
              <div key={i.item_id} data-testid={`archived-${i.item_id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', margin: '5px 0', borderRadius: 10, background: 'rgba(255,255,255,0.04)' }}>
                <span style={{ fontSize: 13 }}>{TYPE_META[i.type]?.[0] || '•'}</span>
                <p style={{ margin: 0, flex: 1, minWidth: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{dm(i.title)} <DemoBadge seed={i.seed} /></p>
                <button data-testid={`restore-${i.item_id}`} onClick={async () => { await req('PATCH', `/api/admin/desk/items/${i.item_id}`, { state: i.type === 'idea' ? 'parked' : 'today' }); setDrawer(drawer.filter((x) => x.item_id !== i.item_id)); showToast(`↩ Restored "${i.title}"`); load(); }}
                  style={{ ...mini, padding: '2px 10px', fontSize: 10.5, color: TEAL, borderColor: 'rgba(84,209,151,0.4)' }}>Restore</button>
                <button data-testid={`delete-forever-${i.item_id}`}
                  onClick={async () => {
                    if (!window.confirm(`Permanently delete "${i.title}"? This cannot be undone.`)) return;
                    try {
                      await req('DELETE', `/api/admin/desk/items/${i.item_id}?confirm=true`);
                      setDrawer(drawer.filter((x) => x.item_id !== i.item_id));
                      showToast(`🗑 Deleted "${i.title}"`);
                      load();
                    } catch (e) { showToast('Delete failed: ' + (e?.message || 'unknown')); }
                  }}
                  style={{ ...mini, padding: '2px 10px', fontSize: 10.5, color: MAGENTA, borderColor: 'rgba(236,124,36,0.4)' }}>🗑</button>
              </div>
            ))}
            {drawer.length === 0 && <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: SUB }}>Nothing archived.</p>}
          </div>
        </div>
      )}

      {/* UNDO TOAST */}
      {toast && (
        <div data-testid="desk-toast" style={{ position: 'fixed', bottom: 22, left: '50%', transform: 'translateX(-50%)', zIndex: 80, display: 'flex', gap: 10, alignItems: 'center', background: '#17181B', border: `1px solid ${BORDER}`, borderRadius: 999, padding: '9px 18px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', animation: anim ? 'desk-pop 220ms ease-out' : 'none' }}>
          <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, fontWeight: 600, color: TXT }}>{dm(toast.msg)}</span>
          {toast.undo && <button data-testid="toast-undo" onClick={() => { toast.undo(); setToast(null); }} style={{ ...mini, padding: '2px 11px', fontSize: 11, color: TEAL, borderColor: 'rgba(84,209,151,0.45)' }}>Undo</button>}
        </div>
      )}
    </div>
  );
}
